import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { 
  Sparkles, 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Download,
  Share2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';

/**
 * AI Commentary Component for Financial Reports
 * 
 * Generates AI-powered variance analysis and insights
 * when comparing two periods of financial data.
 */
const AICommentary = ({ 
  currentData, 
  comparisonData, 
  reportType = 'pnl',
  currencySymbol = '₦',
  budgetId = null,
  projectId = null,
  onGenerate 
}) => {
  const [commentary, setCommentary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [businessContext, setBusinessContext] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [showContextInput, setShowContextInput] = useState(false);

  const generateCommentary = async () => {
    if (!currentData || !comparisonData) {
      toast.error('Please select a comparison period first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/ai-insights/variance-analysis`, {
        current_period: currentData,
        comparison_period: comparisonData,
        report_type: reportType,
        business_context: businessContext || null,
        budget_id: budgetId || null,
        project_id: projectId || null
      });

      setCommentary(response.data);
      
      if (onGenerate) {
        onGenerate(response.data);
      }

      toast.success('AI analysis generated successfully');
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(err.response?.data?.detail || 'Failed to generate AI analysis');
      toast.error('Failed to generate AI analysis');
    } finally {
      setLoading(false);
    }
  };

  const downloadCommentary = () => {
    if (!commentary) return;

    const formatVariance = (v) => {
      const changePercent = v.change_percent;
      const isNumeric = typeof changePercent === 'number' && !isNaN(changePercent);
      const displayPercent = isNumeric ? `${changePercent.toFixed(1)}%` : changePercent;
      return `• ${v.metric}: ${v.change_amount} (${displayPercent})
  ${v.explanation}`;
    };

    const content = `
AI Financial Analysis Report
Generated: ${new Date(commentary.generated_at).toLocaleString()}
Report Type: ${reportType.toUpperCase()}

EXECUTIVE SUMMARY
${commentary.summary}

KEY INSIGHTS
${commentary.key_insights?.map((insight, i) => `${i + 1}. ${insight}`).join('\n') || 'No insights available'}

SIGNIFICANT VARIANCES
${commentary.variances?.map(formatVariance).join('\n\n') || 'No variances identified'}

RECOMMENDATIONS
${commentary.recommendations?.map((rec, i) => `${i + 1}. ${rec}`).join('\n') || 'No recommendations'}

---
Generated by AkountFly AI
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AI_Analysis_${reportType}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Analysis downloaded');
  };

  // No comparison data yet
  if (!comparisonData) {
    return (
      <Card className="border-dashed border-[#5FC6C5]/50 bg-[#5FC6C5]/5" data-testid="ai-commentary-prompt">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#5FC6C5]" />
          <h3 className="font-semibold text-foreground mb-2">AI-Powered Analysis</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Enable period comparison above to unlock AI-generated insights and variance explanations.
          </p>
          <Badge variant="outline" className="border-[#5FC6C5]/50 text-[#5FC6C5]">
            <Sparkles className="w-3 h-3 mr-1" />
            Powered by GPT-5.2
          </Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#5FC6C5]/30" data-testid="ai-commentary" data-pdf-hide={commentary ? undefined : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5FC6C5] to-[#108981] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">{budgetId ? 'AI Variance Analysis. Actual vs Budget' : 'AI Variance Analysis'}</CardTitle>
              <CardDescription className="text-xs">
                {commentary ? `Generated ${new Date(commentary.generated_at).toLocaleString()}` : (budgetId ? 'Analyses performance against your budget\u2019s assumptions & drivers' : 'Click to generate insights')}
              </CardDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {!commentary && (
              <Button 
                onClick={generateCommentary} 
                disabled={loading}
                className="bg-gradient-to-r from-[#5FC6C5] to-[#108981] hover:from-[#108981] hover:to-[#5FC6C5]"
                data-testid="generate-ai-commentary-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Commentary
                  </>
                )}
              </Button>
            )}
            
            {commentary && (
              <>
                <Button variant="outline" size="sm" onClick={downloadCommentary}>
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={generateCommentary}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Business Context Input (Optional) */}
        {!commentary && (
          <Collapsible open={showContextInput} onOpenChange={setShowContextInput}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between mb-3 text-muted-foreground">
                <span>Add business context for better analysis</span>
                {showContextInput ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Textarea
                placeholder="E.g., We launched a new product line in January, experienced supply chain delays in February..."
                value={businessContext}
                onChange={(e) => setBusinessContext(e.target.value)}
                className="mb-3 text-sm"
                rows={3}
              />
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600 mb-3">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Commentary Display */}
        {commentary && (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-2 rounded-lg -mx-2">
                <span className="font-medium text-sm">Analysis Results</span>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="space-y-4 pt-2">
              {/* Executive Summary */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  {commentary.summary}
                </p>
              </div>

              {/* Key Insights */}
              {commentary.key_insights?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Key Insights
                  </h4>
                  <ul className="space-y-2">
                    {commentary.key_insights.map((insight, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-[#5FC6C5]">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Significant Variances */}
              {commentary.variances?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Significant Variances</h4>
                  <div className="space-y-2">
                    {commentary.variances.map((variance, idx) => {
                      // Handle both numeric and string change_percent values
                      const changePercent = variance.change_percent;
                      const isNumeric = typeof changePercent === 'number' && !isNaN(changePercent);
                      const isPositive = isNumeric ? changePercent >= 0 : false;
                      const displayPercent = isNumeric ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%` : changePercent;
                      
                      return (
                        <div key={idx} className="p-2 border rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{variance.metric}</span>
                            <Badge 
                              variant="outline" 
                              className={isNumeric ? (isPositive ? 'text-green-600 border-green-200' : 'text-red-600 border-red-200') : 'text-muted-foreground border-muted'}
                            >
                              {isNumeric && (isPositive ? (
                                <TrendingUp className="w-3 h-3 mr-1" />
                              ) : (
                                <TrendingDown className="w-3 h-3 mr-1" />
                              ))}
                              {displayPercent}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{variance.explanation}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {commentary.recommendations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#5FC6C5]" />
                    Recommendations
                  </h4>
                  <ul className="space-y-2">
                    {commentary.recommendations.map((rec, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="w-5 h-5 rounded-full bg-[#5FC6C5]/20 text-[#5FC6C5] flex items-center justify-center flex-shrink-0 text-xs font-medium">
                          {idx + 1}
                        </span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Model Badge */}
              <div className="flex items-center justify-end pt-2 border-t">
                <Badge variant="outline" className="text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {commentary.model || 'GPT-5.2'}
                </Badge>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};

export default AICommentary;
